/**
 * GULCAN FILTER ENHANCEMENTS
 * Accordion behavior + "meer tonen" truncation for collection sidebar filters.
 * Loaded globally on all collection pages via gulcan-collection-swiper section.
 *
 * Works with both:
 * - Standard collection sidebar (#CollectionSidebar)
 * - Shop page filters (.gulcan-shop__filters)
 *
 * Targets Impulse theme's filter structure:
 *   .collection-sidebar__group > .collapsible-trigger + .collapsible-content
 *   ul.tag-list > li.tag
 */
(function() {
  'use strict';

  var VISIBLE_COUNT = 6;
  var processing = false;

  /* Filter label translations — backup for cases where the labels in
     Shopify admin > Search & Discovery weren't renamed to Dutch yet.
     Keys are uppercased + trimmed to match however the theme renders them. */
  var LABEL_TRANSLATIONS = {
    'AVAILABILITY': 'Beschikbaarheid',
    'PRICE': 'Prijs',
    'BRAND': 'Merk',
    'COLOR': 'Kleur',
    'SIZE': 'Maat',
    'PRODUCT TYPE': 'Producttype',
    'PRODUCTTYPE': 'Producttype'
  };

  /**
   * Find the filter container — works on standard collection pages
   * and the custom shop page layout.
   */
  function getFilterContainer() {
    return document.getElementById('CollectionSidebar')
        || document.querySelector('.gulcan-shop__filters');
  }

  /**
   * Walk the filter group titles and swap English labels for Dutch.
   * Only replaces the text node that holds the label, so the chevron
   * icon (rendered as a sibling <span>) stays intact.
   */
  function translateFilterLabels() {
    var container = getFilterContainer();
    if (!container) return;

    var triggers = container.querySelectorAll('.collapsible-trigger');
    triggers.forEach(function(trigger) {
      for (var i = 0; i < trigger.childNodes.length; i++) {
        var node = trigger.childNodes[i];
        if (node.nodeType !== Node.TEXT_NODE) continue;
        var raw = node.nodeValue.trim();
        if (!raw) continue;
        var key = raw.toUpperCase();
        if (LABEL_TRANSLATIONS[key]) {
          node.nodeValue = node.nodeValue.replace(raw, LABEL_TRANSLATIONS[key]);
        }
      }
    });
  }

  /**
   * Collapse all filter groups on load. When the theme opens one
   * (adds .is-open), close every other group via MutationObserver.
   */
  function initFilterAccordion() {
    var container = getFilterContainer();
    if (!container) return;

    var groups = container.querySelectorAll('.collection-sidebar__group');
    if (!groups.length) return;

    /* Collapse all groups on page load */
    groups.forEach(function(group) {
      var trigger = group.querySelector('.collapsible-trigger');
      var content = group.querySelector('.collapsible-content');
      if (!trigger || !content) return;

      trigger.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      content.classList.remove('is-open');
      content.style.height = '0px';
      content.style.overflow = 'hidden';
    });

    /* Watch for theme reopening a group — enforce one-at-a-time */
    var observer = new MutationObserver(function(mutations) {
      if (processing) return;

      mutations.forEach(function(mutation) {
        if (mutation.attributeName !== 'class') return;
        var target = mutation.target;
        if (!target.classList.contains('collapsible-content')) return;
        if (!target.classList.contains('is-open')) return;

        processing = true;

        /* Close every group except the one just opened */
        groups.forEach(function(group) {
          var content = group.querySelector('.collapsible-content');
          if (!content || content === target) return;

          var trigger = group.querySelector('.collapsible-trigger');
          if (content.classList.contains('is-open')) {
            trigger.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            content.classList.remove('is-open');
            content.style.height = '0px';
            content.style.overflow = 'hidden';
          }
        });

        setTimeout(function() { processing = false; }, 100);
      });
    });

    groups.forEach(function(group) {
      var content = group.querySelector('.collapsible-content');
      if (content) {
        observer.observe(content, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  /**
   * Hide filter values beyond the 6th in each tag-list.
   * Adds a "meer tonen" toggle button to expand/collapse.
   */
  function initFilterTruncation() {
    var container = getFilterContainer();
    if (!container) return;

    var tagLists = container.querySelectorAll('ul.tag-list:not(.tag-list--active-tags)');

    tagLists.forEach(function(list) {
      /* Skip if already processed */
      if (list.querySelector('.gulcan-filter-more')) return;

      var items = list.querySelectorAll('li.tag');
      if (items.length <= VISIBLE_COUNT) return;

      var hiddenItems = [];
      items.forEach(function(item, i) {
        if (i >= VISIBLE_COUNT) {
          item.style.display = 'none';
          hiddenItems.push(item);
        }
      });

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gulcan-filter-more';
      btn.textContent = '+ ' + hiddenItems.length + ' meer tonen';
      var expanded = false;

      btn.addEventListener('click', function() {
        expanded = !expanded;
        hiddenItems.forEach(function(item) {
          item.style.display = expanded ? '' : 'none';
        });
        btn.textContent = expanded
          ? 'Minder tonen'
          : '+ ' + hiddenItems.length + ' meer tonen';

        /* The parent accordion has an explicit inline height (set by the
           theme when it opens). Toggling display on extra items doesn't
           trigger a recalc, so new items render but get clipped. Re-measure
           and update the inline height so the accordion grows/shrinks
           in place without needing a close+reopen. */
        var collapsibleContent = btn.closest('.collapsible-content');
        if (collapsibleContent && collapsibleContent.classList.contains('is-open')) {
          var previousHeight = collapsibleContent.style.height;
          collapsibleContent.style.height = 'auto';
          var newHeight = collapsibleContent.scrollHeight;
          collapsibleContent.style.height = previousHeight;
          /* Force reflow so the transition runs from the old value */
          void collapsibleContent.offsetHeight;
          collapsibleContent.style.height = newHeight + 'px';
        }
      });

      list.after(btn);
    });
  }

  function init() {
    initFilterAccordion();
    initFilterTruncation();
    translateFilterLabels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme re-renders the filter sidebar via AJAX on every filter change
     (price slider, checkbox toggle, sort). Re-run init so Dutch labels
     and the "meer tonen" truncation persist after each re-render.
     Both initFilterTruncation and translateFilterLabels are idempotent. */
  document.addEventListener('ajaxinate:loaded', init);
})();
